import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import ModalV2 from '/@/components/bayer/modal-v2';
import settings from '/@/core/settings';
import {
    useAdvisorRvLinks,
    useAncestors,
    useCheckCuitAvailability,
    useCheckEmailAvailability,
    useGetUserNode,
    useOwnUserNode,
    useUpdateUserNode,
    useUserNodes,
} from '/@/hooks/nodes';
import { useIsValidatedAdvisor, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getGrowerIdentifier, getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import AREA_CODES from '/@/utils/area-codes';
import { formatPhoneNumber, validatePhoneNumber } from '/@/utils/phone-numbers';
import CuitAlert from '../new-growers-modal-v2/add-grower-section/cuit-alert';
import CuitTextField, { CUIT_LENGTH, validateCuit } from '../new-growers-modal-v2/add-grower-section/cuit-text-field';
import EmailIdentifierTextField, {
    validateGrowerIdentifierEmail,
} from '../new-growers-modal-v2/add-grower-section/email-identifier-text-field';
import { validateGrowerEmail } from '../new-growers-modal-v2/add-grower-section/grower-form';
import {
    getGrowerIdentifierCopy,
    type IdentifierFieldStatus,
    isIdentifierStatusBlockingSave,
    normalizeGrowerIdentifier,
} from '../new-growers-modal-v2/add-grower-section/grower-identifier';
import {
    getIdentifierStatusHelperText,
    IdentifierStatusAdornment,
    isIdentifierStatusError,
} from '../new-growers-modal-v2/add-grower-section/identifier-status-adornment';
import FieldviewSection from './fieldview-section';
import TeamAccordion, { type GrowerTeamRow } from './team-accordion';

type EditGrowerFormValues = {
    /** Account identifier (CUIT digits or email); registered in RHF so it counts towards isDirty/isValid. */
    identifier: string;
    subtype: string;
    name: string;
    email: string;
    phone: string | undefined;
    location: string | undefined;
};

const FieldGrowerModal = ({
    open,
    onClose,
    growerNodeId,
}: {
    open: boolean;
    onClose: () => void;
    growerNodeId: number;
}) => {
    const { data: grower } = useGetUserNode(growerNodeId);
    const rvUserQuery = useOwnUserNode();
    const allGrowersQuery = useUserNodes({
        node: rvUserQuery.data?.id,
        page: 1,
        page_size: 10000,
        type: 'grower',
    });
    const theme = useTheme();
    const country = useSelector(getSessionCountry);
    const growerIdentifier = useSelector(getGrowerIdentifier);
    const isEmailIdentifier = growerIdentifier === 'email';
    const identifierCopy = getGrowerIdentifierCopy(growerIdentifier);

    const cuitAvailabilityMutation = useCheckCuitAvailability();
    const emailAvailabilityMutation = useCheckEmailAvailability();

    const [identifierStatus, setIdentifierStatus] = useState<IdentifierFieldStatus>('idle');
    const [identifierFormatError, setIdentifierFormatError] = useState<string | undefined>();
    const [validBusinessName, setValidBusinessName] = useState<string | undefined>();
    const validateRequestId = useRef(0);
    // Read by the RHF validate rule, which must see the current status rather than the
    // one captured when the field registered.
    const identifierStatusRef = useRef(identifierStatus);
    identifierStatusRef.current = identifierStatus;

    const isRV = useIsValidatedRV();
    const isAdvisor = useIsValidatedAdvisor();

    const ancestorsQuery = useAncestors({ node: growerNodeId });
    const advisorRvLinksQuery = useAdvisorRvLinks();
    const hasLinkedRV = (advisorRvLinksQuery.data?.results?.length ?? 0) > 0;
    const canEditSubtype = isAdvisor && (grower?.subtype === 'common' || !hasLinkedRV);
    const hasTeam = grower?.subtype === 'premium';

    const teamData = useMemo((): GrowerTeamRow[] => {
        if (!grower || !ancestorsQuery.data || !hasTeam) return [];

        const rows: GrowerTeamRow[] = [];

        const distributor = ancestorsQuery.data?.find((a) => a.type === 'other' && a.name);
        const squadName = ancestorsQuery.data?.find((a) => a?.type === 'squad')?.unassignedUserData?.name || '';
        const rvParent = ancestorsQuery.data?.find((n) => n?.type === 'rv');
        const advisorParent = ancestorsQuery.data?.find((n) => n?.type === 'advisor');
        const consultantParent = ancestorsQuery.data?.find((n) => n?.type === 'consultant');

        if (consultantParent) {
            rows.push({
                ...consultantParent,
                distributorOrSquad: squadName || '',
                role: _('Consultor'),
            });
        }

        if (isRV && advisorParent) {
            rows.push({
                ...advisorParent,
                distributorOrSquad: distributor?.name || '',
                role: _('Asesor'),
            });
        }

        if (isAdvisor && rvParent) {
            rows.push({
                ...rvParent,
                distributorOrSquad: squadName || '',
                role: 'RV',
            });
        }

        return rows;
    }, [grower, ancestorsQuery.data, isRV, isAdvisor, hasTeam]);

    const form = useForm<EditGrowerFormValues>({
        mode: 'onChange',
        defaultValues: {
            identifier: '',
            subtype: '',
            name: '',
            email: '',
            phone: undefined,
            location: undefined,
        },
    });

    const originalIdentifier = normalizeGrowerIdentifier(
        growerIdentifier,
        isEmailIdentifier ? (grower?.unassignedUserData?.email ?? '') : (grower?.cuit ?? '')
    );

    useEffect(() => {
        if (grower) {
            form.reset({
                identifier: originalIdentifier,
                subtype: grower?.subtype ?? '',
                name: grower?.unassignedUserData?.name ?? '',
                email: grower?.unassignedUserData?.email ?? '',
                phone:
                    grower?.unassignedUserData?.phone && grower?.unassignedUserData?.phone !== ''
                        ? formatPhoneNumber(grower.unassignedUserData.phone)
                        : undefined,
                location:
                    grower?.unassignedUserData?.location && grower?.unassignedUserData?.location !== ''
                        ? grower?.unassignedUserData?.location
                        : undefined,
            });
            setIdentifierStatus('idle');
            setIdentifierFormatError(undefined);
            setValidBusinessName(undefined);
            cuitAvailabilityMutation.reset();
            emailAvailabilityMutation.reset();
        }
    }, [open, grower, isEmailIdentifier]);

    // The identifier's validity comes from the availability request, not from its value, so
    // re-run its rule whenever the status changes. Scoped to that field on purpose: a full
    // form.trigger() would paint errors on fields the user has not touched.
    // biome-ignore lint/correctness/useExhaustiveDependencies: identifierStatus is this effect's trigger, not a value read inside it — the validate rule reads it via identifierStatusRef
    useEffect(() => {
        void form.trigger('identifier');
    }, [identifierStatus, form.trigger]);

    const updateUserNodeMutation = useUpdateUserNode();

    const normalizeIdentifier = useCallback(
        (value: string) => normalizeGrowerIdentifier(growerIdentifier, value),
        [growerIdentifier]
    );

    const verifyIdentifier = useCallback(
        async (rawValue: string) => {
            const valueToCheck = normalizeIdentifier(rawValue);

            // Unchanged from saved value — do not re-validate or block save.
            if (valueToCheck === originalIdentifier) {
                setIdentifierStatus('idle');
                setIdentifierFormatError(undefined);
                setValidBusinessName(undefined);
                return;
            }

            const formatError = isEmailIdentifier
                ? validateGrowerIdentifierEmail(rawValue)
                : validateCuit(valueToCheck);
            if (formatError) {
                setIdentifierStatus('invalidFormat');
                setIdentifierFormatError(formatError);
                setValidBusinessName(undefined);
                return;
            }

            const requestId = ++validateRequestId.current;
            setIdentifierStatus('verifying');
            setIdentifierFormatError(undefined);
            setValidBusinessName(undefined);

            try {
                const result = isEmailIdentifier
                    ? await emailAvailabilityMutation.trigger(valueToCheck)
                    : await cuitAvailabilityMutation.trigger(valueToCheck);

                if (requestId !== validateRequestId.current) return;

                if (result.status === 'available') {
                    setIdentifierStatus('valid');
                    setValidBusinessName(undefined);
                    return;
                }
                if (result.status === 'assigned_to_you' && result.userNode.id === growerNodeId) {
                    setIdentifierStatus('valid');
                    setValidBusinessName(result.userNode.name || undefined);
                    return;
                }
                setIdentifierStatus('duplicate');
                setValidBusinessName(undefined);
            } catch {
                if (requestId !== validateRequestId.current) return;
                setIdentifierStatus('connectionError');
                setValidBusinessName(undefined);
            }
        },
        [
            normalizeIdentifier,
            originalIdentifier,
            isEmailIdentifier,
            emailAvailabilityMutation.trigger,
            cuitAvailabilityMutation.trigger,
            growerNodeId,
        ]
    );

    /** Status side of a value change; the RHF field owns the value itself. */
    const handleIdentifierChanged = (next: string) => {
        setIdentifierFormatError(undefined);
        setValidBusinessName(undefined);
        // Back to the saved value: nothing to re-check. Otherwise block save until Validar succeeds.
        setIdentifierStatus(normalizeIdentifier(next) === originalIdentifier ? 'idle' : 'pending');
    };

    /** Locked after successful validation (or original value). Unlock with "Cambiar". */
    const identifierLocked = identifierStatus === 'idle' || identifierStatus === 'valid';

    const handleValidateOrChangeIdentifier = () => {
        if (identifierLocked) {
            setIdentifierStatus('pending');
            setIdentifierFormatError(undefined);
            setValidBusinessName(undefined);
            cuitAvailabilityMutation.reset();
            emailAvailabilityMutation.reset();
            return;
        }
        void verifyIdentifier(form.getValues('identifier'));
    };

    const onSubmit = async (data: EditGrowerFormValues) => {
        const normalized = normalizeIdentifier(data.identifier);
        const identifierOk = normalized === originalIdentifier || identifierStatus === 'valid';
        if (!identifierOk) {
            await verifyIdentifier(data.identifier);
            return;
        }

        try {
            await updateUserNodeMutation.trigger({
                id: growerNodeId,
                data: {
                    subtype: data.subtype as 'common' | 'premium',
                    ...(isEmailIdentifier ? {} : { cuit: normalized || undefined }),
                    unassignedUserData: {
                        name: data.name,
                        email: isEmailIdentifier ? normalized : data.email,
                        phone: data.phone,
                        location: data.location,
                    },
                },
            });
            mpTrackEvent('Grower edited', undefined);
            enqueueSnackbar(_('Productor actualizado correctamente'), { variant: 'success' });
            onClose();
        } catch {
            enqueueSnackbar(_('Error al actualizar el productor'), { variant: 'error' });
        }
    };

    // The identifier is a registered field, so isDirty/isValid already account for it.
    const { isDirty, isValid } = form.formState;
    const canSaveFinal = isDirty && isValid && !updateUserNodeMutation.isMutating;

    const growerFieldviewData = grower?.user;
    console.log(updateUserNodeMutation.isMutating, !form.formState.isDirty, !form.formState.isValid);

    const identifierValue = form.watch('identifier');
    const identifierHelperText = getIdentifierStatusHelperText({
        identifier: growerIdentifier,
        status: identifierStatus,
        businessName: validBusinessName,
        digitCount: identifierValue.length,
        cuitLength: CUIT_LENGTH,
        formatErrorMessage: identifierFormatError,
    });
    const identifierAdornment = <IdentifierStatusAdornment status={identifierStatus} />;
    const isIdentifierMutating = identifierStatus === 'verifying';

    /**
     * Same alert the create flow shows: who holds the identifier and what to do about it.
     * Only while the status is 'duplicate' -- any edit to the field moves it to 'pending',
     * so the alert can never describe a value other than the one on screen.
     */
    const availabilityResult = isEmailIdentifier ? emailAvailabilityMutation.data : cuitAvailabilityMutation.data;
    const showAvailabilityAlert = identifierStatus === 'duplicate' && !!availabilityResult;

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            title={_('Información del productor')}
            maxWidth='md'
            bodyProps={{ sx: { p: 3, maxHeight: '80vh', overflow: 'auto' } }}
            actions={
                <Stack direction='row' spacing={1}>
                    <Button
                        type='button'
                        variant='text'
                        color='secondary'
                        onClick={onClose}
                        disabled={updateUserNodeMutation.isMutating}
                    >
                        {_('Cancelar')}
                    </Button>
                    <Button
                        type='submit'
                        variant='contained'
                        color='secondary'
                        disabled={!canSaveFinal}
                        loading={updateUserNodeMutation.isMutating || isIdentifierMutating}
                        onClick={form.handleSubmit(onSubmit)}
                    >
                        {_('Guardar cambios')}
                    </Button>
                </Stack>
            }
            actionsProps={{
                justifyContent: 'end',
            }}
        >
            <FormProvider {...form}>
                <Stack className='mp-mask' spacing={3} direction='column' sx={{ width: '100%', pb: 7 }}>
                    <Typography variant='subtitle1' color='primary' sx={{ textAlign: 'start', width: '100%' }}>
                        {_(
                            'Mantené actualizada la información personal del productor y sus datos vinculados en {{appName}}.',
                            { appName: settings.app.name }
                        )}
                    </Typography>
                    <Typography
                        variant='subtitle1'
                        fontWeight='bold'
                        color='primary'
                        sx={{ textAlign: 'start', width: '100%' }}
                    >
                        {_('Datos registrados')}
                    </Typography>
                    {isAdvisor && (
                        <Alert severity='blue'>
                            <Typography color='primary' variant='body1' sx={{ textAlign: 'left' }}>
                                <Box component='span' fontWeight='bold'>
                                    {_('Importante: ')}
                                </Box>
                                {_(
                                    'Si cambias el segmento a Premium, los datos del productor van a ser compartidos con el RV al cual estés vinculado. Una vez realizado el cambio, no podrás revertirlo.'
                                )}
                            </Typography>
                        </Alert>
                    )}

                    <Stack direction='row' spacing={2} width='100%' alignItems='flex-start'>
                        <Stack sx={{ flex: 1, minWidth: 0 }}>
                            <Controller
                                name='identifier'
                                control={form.control}
                                rules={{
                                    // Validity is the availability result, not the text: the helper
                                    // text below already explains which state we are in.
                                    validate: () => !isIdentifierStatusBlockingSave(identifierStatusRef.current),
                                }}
                                render={({ field }) => {
                                    const identifierFieldProps = {
                                        value: field.value,
                                        onChange: (next: string) => {
                                            field.onChange(next);
                                            handleIdentifierChanged(next);
                                        },
                                        onBlur: field.onBlur,
                                        disabled: identifierLocked,
                                        error: isIdentifierStatusError(identifierStatus),
                                        helperText: identifierHelperText,
                                        InputProps: { endAdornment: identifierAdornment },
                                        onKeyDown: (e: KeyboardEvent) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleValidateOrChangeIdentifier();
                                            }
                                        },
                                    };
                                    return isEmailIdentifier ? (
                                        <EmailIdentifierTextField {...identifierFieldProps} />
                                    ) : (
                                        <CuitTextField {...identifierFieldProps} />
                                    );
                                }}
                            />
                        </Stack>
                        <Button
                            type='button'
                            variant='outlined'
                            color='secondary'
                            sx={{ flex: 'none' }}
                            onClick={handleValidateOrChangeIdentifier}
                            loading={isIdentifierMutating}
                            disabled={isIdentifierMutating}
                        >
                            {identifierLocked ? identifierCopy.changeButton : identifierCopy.validateButton}
                        </Button>
                    </Stack>

                    {showAvailabilityAlert && availabilityResult && (
                        <CuitAlert
                            availabilityOutput={availabilityResult}
                            identifierValue={identifierValue}
                            onInvitationSent={() => {
                                enqueueSnackbar(_('Solicitud de vinculación enviada'), { variant: 'success' });
                            }}
                        />
                    )}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 2,
                            width: '100%',
                        }}
                    >
                        <Controller
                            name='subtype'
                            control={form.control}
                            rules={{ required: _('El tipo es requerido') }}
                            render={({ field, fieldState: { error } }) => (
                                <FormControl fullWidth error={!!error} disabled={!canEditSubtype}>
                                    <InputLabel>{_('Segmento')}</InputLabel>
                                    <Select
                                        {...field}
                                        label={_('Segmento')}
                                        sx={{ textAlign: 'left' }}
                                        renderValue={(value) =>
                                            value === 'premium' ? _('Premium') : _('Otros segmentos')
                                        }
                                    >
                                        <MenuItem value='premium'>
                                            <RadioGroup>
                                                <Radio
                                                    value='premium'
                                                    color='secondary'
                                                    checked={field.value === 'premium'}
                                                    onChange={field.onChange}
                                                />
                                            </RadioGroup>
                                            {_('Premium')}
                                        </MenuItem>
                                        <MenuItem value='common'>
                                            <RadioGroup>
                                                <Radio
                                                    value='common'
                                                    color='secondary'
                                                    checked={field.value === 'common'}
                                                    onChange={field.onChange}
                                                />
                                            </RadioGroup>
                                            {_('Otros segmentos')}
                                        </MenuItem>
                                    </Select>
                                    {error && <FormHelperText>{error.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />

                        <Controller
                            name='name'
                            control={form.control}
                            rules={{ required: _('El nombre es requerido') }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label={_('Nombre')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />

                        <Controller
                            name='location'
                            control={form.control}
                            rules={{ required: _('La ubicación es requerida') }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label={_('Ubicación')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />

                        {/* AR only: contact email (not the account identifier). UY uses email as identifier above. */}
                        {!isEmailIdentifier && (
                            <Controller
                                name='email'
                                control={form.control}
                                rules={{
                                    required: _('El mail es requerido'),
                                    validate: (v) =>
                                        validateGrowerEmail(
                                            v,
                                            allGrowersQuery.data?.results || [],
                                            rvUserQuery.data,
                                            undefined,
                                            undefined,
                                            growerNodeId
                                        ),
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        label={_('Mail')}
                                        type='email'
                                        fullWidth
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        )}

                        <Controller
                            control={form.control}
                            name='phone'
                            rules={{
                                validate: (v) => {
                                    if (!v || v === '') return true;
                                    return validatePhoneNumber(v);
                                },
                            }}
                            render={({ field, fieldState }) => {
                                return (
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            height: 'auto',
                                        }}
                                    >
                                        <PhoneInput
                                            {...field}
                                            onChange={(_value, _data, _event, formattedValue) => {
                                                field.onChange(formattedValue);
                                            }}
                                            buttonStyle={{
                                                border: 'none',
                                            }}
                                            specialLabel=''
                                            country={country!.code!.toLowerCase()}
                                            inputStyle={{
                                                border: 'none',
                                                height: '100%',
                                                borderRadius: '0',
                                                width: '100%',
                                            }}
                                            containerStyle={{
                                                border: '1px solid #0000003b',
                                                borderRadius: '0',
                                            }}
                                            enableAreaCodeStretch
                                            enableAreaCodes
                                            areaCodes={AREA_CODES}
                                            masks={{ mx: '... .... ....' }}
                                        />
                                        <FormHelperText
                                            sx={{
                                                fontSize: 9,
                                                color: fieldState.error
                                                    ? theme.palette.error.main
                                                    : theme.palette.primary.main,
                                            }}
                                        >
                                            {fieldState.error
                                                ? _('El formato del número de teléfono es incorrecto.')
                                                : ''}
                                        </FormHelperText>
                                    </FormControl>
                                );
                            }}
                        />
                    </Box>
                    {!!growerFieldviewData && <FieldviewSection growerFieldviewData={growerFieldviewData} />}
                    {hasTeam && <TeamAccordion teamData={teamData} />}
                </Stack>
            </FormProvider>
        </ModalV2>
    );
};

export default FieldGrowerModal;
